import type { ComponentType } from 'preact';
import { render } from 'preact';
import { Dialogue } from './Dialogue';
import { DeathOverlay, RewardsOverlay } from './overlays';
import { CharacterCreate, CharacterSelect, LoadingScreen, TitleScreen } from './screens';
import { type PanelId, closePanel, ui } from './store';
import { Window } from './Window';

/** Panels register themselves here so feature modules stay decoupled. */
const PANELS = new Map<PanelId, ComponentType>();

export function registerPanel(id: PanelId, component: ComponentType): void {
  PANELS.set(id, component);
}

/** Always-mounted in-game overlays (chat, invites, trade…) registered by feature modules. */
const OVERLAYS: ComponentType[] = [];

export function registerOverlay(component: ComponentType): void {
  if (!OVERLAYS.includes(component)) OVERLAYS.push(component);
}

function PanelHost() {
  const id = ui.panel.value;
  void ui.version.value;
  if (!id) return null;
  const C = PANELS.get(id);
  if (!C)
    return (
      <Window title="Coming soon" width={420}>
        <p>This panel is not available yet.</p>
      </Window>
    );
  return <C />;
}

function Confirm() {
  const c = ui.confirm.value;
  if (!c) return null;
  return (
    <div class="modal-scrim interactive">
      <div class="panel confirm">
        <div class="panel__title">Confirm</div>
        <p style={{ padding: '14px 16px' }}>{c.text}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '0 14px 14px' }}>
          <button class="btn" onClick={() => (ui.confirm.value = null)}>
            Cancel
          </button>
          <button
            class="btn btn--gold"
            onClick={() => {
              c.ok();
              ui.confirm.value = null;
            }}
          >
            {c.okLabel ?? 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Root() {
  const s = ui.screen.value;
  if (s === 'boot') return <LoadingScreen />;
  if (s === 'title') return <TitleScreen />;
  if (s === 'select') return <CharacterSelect />;
  if (s === 'create') return <CharacterCreate />;
  if (s === 'loading') return <LoadingScreen />;
  return (
    <>
      {OVERLAYS.map((C, i) => (
        <C key={i} />
      ))}
      <PanelHost />
      <Dialogue />
      <DeathOverlay />
      <RewardsOverlay />
      <Confirm />
    </>
  );
}

export function mountUi(container: HTMLElement): void {
  const host = document.createElement('div');
  host.id = 'ui-app';
  host.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:20';
  container.appendChild(host);
  render(<Root />, host);
}

export { closePanel };
